import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

// Projets en direct, côté navigateur : l'adresse des WebSockets sous /api (même origine), la
// couleur de chacun (curseur, pastille), et le suivi d'un projet ouvert : qui le regarde, et
// une relecture de la fiche à chaque changement signalé par le serveur.

/** Adresse WebSocket d'un chemin de l'API, sur l'origine de la page. */
export function adresseWebSocket(chemin: string, lieu: { protocol: string; host: string }) {
  return `${lieu.protocol === 'https:' ? 'wss' : 'ws'}://${lieu.host}${chemin}`
}

/**
 * Couleur stable d'une personne (teinte tirée de son identifiant ; saturation et clarté fixes,
 * lisibles en clair comme en sombre), en #rrggbb : le curseur de l'éditeur n'accepte que cette
 * forme (sinon il le trace en transparent).
 */
export function couleurPersonne(id: string): string {
  let h = 0
  for (const c of id) h = (h * 31 + c.charCodeAt(0)) % 360
  const s = 0.62
  const l = 0.46
  const a = s * Math.min(l, 1 - l)
  const canal = (n: number) => {
    const k = (n + h / 30) % 12
    const v = l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1))
    return Math.round(v * 255)
      .toString(16)
      .padStart(2, '0')
  }
  return `#${canal(0)}${canal(8)}${canal(4)}`
}

export type Presence = { id: string; nom: string }

/**
 * Suit un projet ouvert : présence des personnes et relecture de ses requêtes (fiche, listes de
 * tâches) à chaque changement ; reconnexion seule après une coupure, avec une relecture (des
 * changements ont pu passer pendant).
 */
export function useDirectProjet(projetId: string) {
  const client = useQueryClient()
  const [presents, setPresents] = useState<Presence[]>([])
  const [connecte, setConnecte] = useState(false)
  useEffect(() => {
    let ws: WebSocket | null = null
    let fini = false
    let attente = 500
    let minuteur: ReturnType<typeof setTimeout> | undefined
    const relire = () => {
      void client.invalidateQueries({ queryKey: ['projets'] })
      void client.invalidateQueries({ queryKey: ['taches'] })
      void client.invalidateQueries({ queryKey: ['brouillons'] })
    }
    const ouvrir = (reprise: boolean) => {
      ws = new WebSocket(adresseWebSocket(`/api/projets/${projetId}/direct`, window.location))
      ws.onopen = () => {
        attente = 500
        setConnecte(true)
        if (reprise) relire()
      }
      ws.onmessage = (e) => {
        const m = JSON.parse(String(e.data)) as
          { type: 'presence'; personnes: Presence[] } | { type: 'change' }
        if (m.type === 'presence') setPresents(m.personnes)
        else relire()
      }
      ws.onclose = () => {
        setConnecte(false)
        if (fini) return
        minuteur = setTimeout(() => ouvrir(true), attente)
        attente = Math.min(attente * 2, 15_000)
      }
    }
    ouvrir(false)
    return () => {
      fini = true
      clearTimeout(minuteur)
      ws?.close()
    }
  }, [projetId, client])
  return { presents, connecte }
}
